import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';

import { KilogramsAndGramsWeightInput } from './kilograms-and-grams-weight-input/kilograms-and-grams-weight-input';
import { OuncesWeightInput } from './ounces-weight-input';
import style from './style.module.less';
import { WeightInputUnit } from './units';
import { UnitDisplayType } from '../../../enums/units/unit-display-type';
import { UnitPosition } from '../../../enums/units/unit-position';
import { testId } from '../../services/testid-service';

/**
 * @typedef {import('../../../enums/units/unit-display-types.d.ts').UnitDisplayType} UnitDisplayTypeType
 */

/**
 * @typedef {import('../../../enums/units/unit-display-types.d.ts').UnitPosition} UnitPositionType
 */

/**
 * @typedef {import('../../prop-types/weight-unit.d.ts').WeightUnit} WeightUnit
 */

/**
 * @param {object} props
 * @param {string} [props.unit]
 * @param {string | null} [props.inputClassName]
 * @param {string | null} [props.className]
 * @param {number | null} [props.weight]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.shrinkable]
 * @param {(value: number, weightUnit: WeightUnit) => void} props.onChange
 * @param {UnitDisplayTypeType} [props.unitDisplayType]
 * @param {UnitPositionType} [props.unitPosition]
 * @param {string} [props.numberInputContainerClassName]
 * @param {string} [props.weightInputInnerContainerClassName]
 * @param {string} [props.labelId]
 * @returns {React.JSX.Element}
 */
export const WeightInput = ({
  unit = WeightInputUnit.ounces,
  inputClassName = null,
  className = null,
  weight = null,
  disabled = false,
  shrinkable = false,
  onChange,
  unitDisplayType = UnitDisplayType.AbbreviatedWithParentheses,
  unitPosition = UnitPosition.OutsideEnd,
  numberInputContainerClassName,
  weightInputInnerContainerClassName,
  labelId,
}) => {
  const onChangeWeightValue = useCallback(
    (weightValue) => {
      onChange(weightValue, unit);
    },
    [onChange, unit],
  );

  const WeightComponent = useMemo(
    () => (unit?.toLowerCase() === WeightInputUnit.grams ? KilogramsAndGramsWeightInput : OuncesWeightInput),
    [unit],
  );

  return (
    <div {...testId('weight-input')} className={classnames(style.weight, className)}>
      <div className={classnames(style.weight, weightInputInnerContainerClassName)}>
        <WeightComponent
          disabled={disabled}
          inputClassName={inputClassName}
          labelId={labelId}
          numberInputContainerClassName={numberInputContainerClassName}
          onChange={onChangeWeightValue}
          shrinkable={shrinkable}
          unitDisplayType={unitDisplayType}
          unitPosition={unitPosition}
          weightValue={weight}
        />
      </div>
    </div>
  );
};
